import {useCallback, useEffect, useState} from 'react';
import {useApolloClient} from '@apollo/client';

import {useAdminSchools} from './useAdminSchools';
import type {AdminSchool} from './useAdminSchools';
import {AdminSchoolContentLocksDocument} from './adminContentLocks.queries.generated';
import type {
  AdminSchoolContentLocksQuery,
  AdminSchoolContentLocksQueryVariables
} from './adminContentLocks.queries.generated';

export interface RestrictionBreakdown {
  restricted: AdminSchool[];
  unrestricted: AdminSchool[];
}

interface UseAdminContentLocksOptions {
  /** Skip fetching when the caller isn't an admin / the feature is off. Defaults to true. */
  enabled?: boolean;
}

interface UseAdminContentLocksResult {
  schools: AdminSchool[];
  /** True until both the school list and every school's locks have resolved. */
  loading: boolean;
  /**
   * Splits the admin's schools by whether they lock the given source(s). Pass a single id or a list
   * (the level plus its ancestors) — a school counts if it locks ANY id, so a parent lock cascades.
   */
  getBreakdown: (source?: string | string[]) => RestrictionBreakdown;
  /** Like getBreakdown but with an arbitrary predicate over a school's locked ids (for tree-aware checks). */
  getBreakdownWhere: (predicate: (lockedSourceIds: Set<string>) => boolean) => RestrictionBreakdown;
  getLockedSourceIds: (schoolId: string) => Set<string>;
  /** The existing ContentLock id for a (school, source) pair — needed to `unlockContent` (destroy-by-id). */
  getLockId: (schoolId: string, sourceId: string) => string | undefined;
}

/**
 * Real per-school restriction state for the admin-locked-assessments indicator (Part 2).
 *
 * Fetches the admin's schools and *watches* each school's content-lock set (one watcher per school).
 * Using `watchQuery` rather than a one-shot read makes every indicator reactive: when the modal
 * locks/unlocks content and refetches these queries, the watchers emit and the indicators re-render
 * with no page reload.
 *
 * Perf note: every shield runs this hook, so a dense page mounts N×M (indicators × admin schools)
 * `watchQuery` subscriptions. Apollo shares the network + cache across them and React batches the
 * state updates, so this is fine for the usual handful of admin schools. If density is ever *measured*
 * to be a problem, hoist the subscriptions into a single context provider (read via `useContext`).
 */
export function useAdminContentLocks({
  enabled = true
}: UseAdminContentLocksOptions = {}): UseAdminContentLocksResult {
  const {schools, loading: schoolsLoading} = useAdminSchools();
  const client = useApolloClient();
  // schoolId -> (locked sourceId -> ContentLock id). The lock id is kept so the modal can unlock by id.
  const [lockedBySchool, setLockedBySchool] = useState<Map<string, Map<string, string>>>(
    () => new Map()
  );

  // Stable dependency for the fetch effect (the schools array identity changes each render).
  const schoolIdsKey = schools.map((school) => school.id).join(',');

  useEffect(() => {
    if (!enabled || schoolsLoading || schools.length === 0) {
      return undefined;
    }

    const subscriptions = schools.map((school) =>
      client
        .watchQuery<AdminSchoolContentLocksQuery, AdminSchoolContentLocksQueryVariables>({
          query: AdminSchoolContentLocksDocument,
          variables: {schoolId: school.id},
          fetchPolicy: 'cache-first'
        })
        .subscribe({
          next: ({data}) => {
            const locks = new Map(
              (data?.contentLocks ?? []).map((lock) => [lock.sourceId, lock.id])
            );
            setLockedBySchool((prev) => new Map(prev).set(school.id, locks));
          },
          // A failed fetch is treated as "no known locks" (and still marks the school resolved) so one
          // bad request doesn't wedge loading. Logged so this fail-open stays observable — otherwise a
          // failed query is indistinguishable from "no restrictions" in the indicator.
          error: (err) => {
            logger.error('Failed to fetch content locks for school %s: %o', school.id, err);
            setLockedBySchool((prev) => new Map(prev).set(school.id, new Map()));
          }
        })
    );

    return () => {
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
    // schoolIdsKey captures the schools dependency without churning on array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, schoolsLoading, schoolIdsKey, client]);

  const getLockedSourceIds = useCallback(
    (schoolId: string): Set<string> => new Set(lockedBySchool.get(schoolId)?.keys() ?? []),
    [lockedBySchool]
  );

  const getBreakdownWhere = useCallback(
    (predicate: (lockedSourceIds: Set<string>) => boolean): RestrictionBreakdown => {
      const restricted: AdminSchool[] = [];
      const unrestricted: AdminSchool[] = [];
      if (enabled) {
        schools.forEach((school) => {
          if (predicate(getLockedSourceIds(school.id))) {
            restricted.push(school);
          } else {
            unrestricted.push(school);
          }
        });
      }
      return {restricted, unrestricted};
    },
    [enabled, schools, getLockedSourceIds]
  );

  const getBreakdown = useCallback(
    (source?: string | string[]): RestrictionBreakdown => {
      const sourceIds = source == null ? [] : Array.isArray(source) ? source : [source];
      if (sourceIds.length === 0) {
        return {restricted: [], unrestricted: []};
      }
      return getBreakdownWhere((lockedSourceIds) => sourceIds.some((id) => lockedSourceIds.has(id)));
    },
    [getBreakdownWhere]
  );

  const getLockId = useCallback(
    (schoolId: string, sourceId: string): string | undefined =>
      lockedBySchool.get(schoolId)?.get(sourceId),
    [lockedBySchool]
  );

  const locksLoaded = schools.every((school) => lockedBySchool.has(school.id));
  return {
    schools: enabled ? schools : [],
    loading: enabled && (schoolsLoading || !locksLoaded),
    getBreakdown,
    getBreakdownWhere,
    getLockedSourceIds,
    getLockId
  };
}
